import { Col, Divider } from 'antd';
import { FormItem, BadgeAsterisk } from 'modules/common/components';
import styles from 'modules/donations/components/CreateDonationForm/styles.module.scss';
import { isFailedDonation } from 'modules/donations/utils/isFailedDonation';
import { FC, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { QuantityFields } from './enum';
import { getQuantityFields } from './fields';

type Props = {
  fieldNamePrefix: string;
  enabled: boolean;
  sectionNamePrefix?: string;
};

const QuantitySection: FC<Props> = ({
  fieldNamePrefix,
  enabled,
  sectionNamePrefix,
}) => {
  const { t } = useTranslation('donation.CreateDonationForm.Quantity');
  const { control, formState, getFieldState, watch, setValue } =
    useFormContext();

  const prefix = useMemo(() => {
    const sectionName = sectionNamePrefix ? sectionNamePrefix + '.' : '';
    return `${sectionName}${fieldNamePrefix}`;
  }, [sectionNamePrefix, fieldNamePrefix]);

  const { actual, approvedDonationsMl, unit } = watch(prefix) ?? {};

  const isFailed = isFailedDonation({
    actual,
    approvedMl: approvedDonationsMl,
    units: unit,
  });

  useEffect(() => {
    if (!isFailed) {
      setValue(`${prefix}.failedReason`, undefined);
    }
  }, [isFailed]);

  const allFields = useMemo(() => getQuantityFields({ enabled }), [enabled]);

  const fields = useMemo(() => {
    return allFields.filter(({ name }) => {
      return name !== QuantityFields.failedReason || isFailed;
    });
  }, [allFields, isFailed]);

  return (
    <>
      <Divider plain orientation="left">
        {t('quantity')}
      </Divider>

      {fields.map(({ name, Component, label }) => {
        const fieldName = `${prefix}.${name}`;
        const { error } = getFieldState(fieldName, formState);

        return (
          <Col key={name}>
            <FormItem
              className={styles.formItem}
              extra={error?.message}
              label={label}
              validateStatus={error ? 'error' : undefined}
            >
              <BadgeAsterisk>
                <Component control={control} name={fieldName} />
              </BadgeAsterisk>
            </FormItem>
          </Col>
        );
      })}
    </>
  );
};

export default QuantitySection;
